import { ActionIcon, Button, Menu, Tooltip, UnstyledButton } from '@mantine/core'
import { IconArrowRight, IconBell, IconDots, IconEyeOff } from '@tabler/icons-react'
import dayjs from 'dayjs'
import { BRIEFING_DISMISS_REASONS } from '@/model/middleware-types'
import type { BriefingAction, BriefingAppointment, BriefingItem } from '../../hooks/useBriefingDay'
import {
  actionSentence,
  doneKey,
  formatClock,
  KIND_LABELS,
  OPEN_LABELS,
  quickAction,
  REASON_LABELS,
  urgency,
  URGENCY_STYLE,
  whyLine,
  type Urgency,
} from './briefingCopy'
import { DoneLine, PatientAvatar, QuickButton } from './BriefingParts'

// The three reminder times, in the browser's time zone.
const REMINDERS: ReadonlyArray<{ label: string; at: () => dayjs.Dayjs }> = [
  { label: 'In 1 hour', at: () => dayjs().add(1, 'hour') },
  {
    label: 'Tomorrow at 8:00 AM',
    at: () => dayjs().add(1, 'day').hour(8).minute(0).second(0).millisecond(0),
  },
  {
    label: 'In 1 week at 8:00 AM',
    at: () => dayjs().add(7, 'day').hour(8).minute(0).second(0).millisecond(0),
  },
]

const URGENCY_LABEL: Record<Urgency, string> = {
  urgent: 'Urgent',
  soon: 'Today',
  routine: 'Routine',
}

type BriefingPatientCardProps = {
  // Every open concern for one patient, most urgent first.
  items: BriefingItem[]
  appointment: BriefingAppointment | undefined
  // The provider's local date ('YYYY-MM-DD').
  today: string
  // Its visit is the one selected in the timeline.
  selected: boolean
  onSelectVisit: (appointmentId: string | null) => void
  onOpen: (href: string) => void
  onAction: (item: BriefingItem, change: BriefingAction) => void
  // After "Mark reviewed" / "Mark done", by doneKey: what it says, and Undo when possible.
  done: Map<string, { did: string; canUndo: boolean }>
  quickBusyId: string | null
  onQuick: (item: BriefingItem) => void
  onUndoQuick: (item: BriefingItem) => void
}

/** One patient, with each thing that needs the provider as its own row and action. */
export function BriefingPatientCard({
  items,
  appointment,
  today,
  selected,
  onSelectVisit,
  onOpen,
  onAction,
  done,
  quickBusyId,
  onQuick,
  onUndoQuick,
}: BriefingPatientCardProps) {
  const patient = items[0].patient
  const open = items.filter((item) => !done.has(doneKey(item)))
  const top = urgency(open[0] ?? items[0], today)
  const when = appointment
    ? [formatClock(appointment.start), appointment.visitReason].filter(Boolean).join(' · ')
    : 'No visit today'
  const header = (
    <div className="flex w-full flex-wrap items-center gap-x-2.5 gap-y-1">
      <PatientAvatar name={patient.name} photoUrl={patient.photoUrl} size={36} />
      <div className="min-w-0 flex-1">
        <div className="truncate text-[15px] font-semibold text-gray-900">
          {patient.name ?? 'Unknown patient'}
        </div>
        <div className="text-[13px] text-gray-600">{when}</div>
      </div>
      <span className="shrink-0 text-[13px] font-semibold text-gray-700">
        {open.length > 0 ? `${open.length} ${open.length === 1 ? 'item' : 'items'}` : 'All done'}
      </span>
    </div>
  )

  return (
    <div
      className={`flex flex-col gap-2 rounded-lg border border-l-4 border-gray-200 bg-white px-3 pt-2.5 pb-2 ${
        open.length === 0 ? 'border-l-[#16a34a]' : URGENCY_STYLE[top].edge
      }`}
    >
      {appointment ? (
        <UnstyledButton
          className="text-left"
          aria-pressed={selected}
          onClick={() => onSelectVisit(selected ? null : appointment.appointmentId)}
        >
          {header}
        </UnstyledButton>
      ) : (
        header
      )}
      <ul className="flex flex-col divide-y divide-gray-100">
        {items.map((item) => {
          const key = doneKey(item)
          const isDone = done.get(key)
          const level = urgency(item, today)
          const quick = quickAction(item)
          const sentence = actionSentence(item, today)
          return (
            <li key={item.id} className="grid grid-cols-[minmax(0,1fr)_auto] gap-x-2 py-2">
              <div className="flex min-w-0 flex-col gap-1.5">
                <div className="flex items-start gap-2">
                  <span
                    aria-hidden
                    className={`mt-[7px] h-2 w-2 shrink-0 rounded-full ${
                      isDone ? 'bg-green-600' : URGENCY_STYLE[level].dot
                    }`}
                  />
                  <div className="min-w-0">
                    <div className="text-xs font-semibold text-gray-600">
                      {KIND_LABELS[item.kind]}
                      {!isDone && level === 'urgent' && (
                        <span className="text-[#b23a24]"> · {URGENCY_LABEL[level]}</span>
                      )}
                      {item.patientReported && (
                        <span className="font-normal"> · patient-reported</span>
                      )}
                    </div>
                    <p
                      className={`text-[14px] leading-snug ${
                        isDone ? 'text-gray-500 line-through decoration-[#c7cbd1]' : 'text-gray-900'
                      }`}
                      title={sentence}
                    >
                      {sentence}
                    </p>
                    {!isDone &&
                      (item.kind === 'guidance' ? (
                        // The topic's own reasoning and question, as Risks & Gaps shows them.
                        <>
                          {item.detail && (
                            <p className="mt-0.5 text-[13px] leading-snug text-gray-600">
                              <span className="font-semibold">Why ask:</span> {item.detail}
                            </p>
                          )}
                          {item.question && (
                            <p className="mt-0.5 text-[13px] leading-snug text-gray-600">
                              <span className="font-semibold">Suggested question:</span> “
                              {item.question}”
                            </p>
                          )}
                        </>
                      ) : (
                        <p className="mt-0.5 text-[13px] leading-snug text-gray-500">
                          {whyLine(item)}
                        </p>
                      ))}
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-1.5 pl-4">
                  {isDone ? (
                    <DoneLine
                      did={isDone.did}
                      onUndo={isDone.canUndo ? () => onUndoQuick(item) : null}
                    />
                  ) : (
                    quick && (
                      <QuickButton loading={quickBusyId === item.id} onClick={() => onQuick(item)}>
                        {quick.label}
                      </QuickButton>
                    )
                  )}
                  <Button
                    variant="default"
                    size="compact-xs"
                    h={28}
                    px={10}
                    fz={13}
                    fw={600}
                    radius="md"
                    rightSection={<IconArrowRight size={12} />}
                    onClick={() => onOpen(item.href)}
                  >
                    {OPEN_LABELS[item.kind]}
                  </Button>
                </div>
              </div>
              <Menu position="bottom-end" withinPortal shadow="md">
                {/* Tooltip outside the target: Menu.Target puts the menu's aria props on its child. */}
                <Tooltip label="Dismiss or remind me">
                  <Menu.Target>
                    <ActionIcon
                      variant="subtle"
                      color="gray"
                      size="sm"
                      aria-label="Dismiss or remind me"
                      data-briefing-menu={item.id}
                    >
                      <IconDots size={16} />
                    </ActionIcon>
                  </Menu.Target>
                </Tooltip>
                <Menu.Dropdown>
                  <Menu.Label>Dismiss</Menu.Label>
                  {BRIEFING_DISMISS_REASONS.map((reason) => (
                    <Menu.Item
                      key={reason}
                      leftSection={<IconEyeOff size={14} />}
                      onClick={() => onAction(item, { action: 'dismiss', reason })}
                    >
                      {REASON_LABELS[reason]}
                    </Menu.Item>
                  ))}
                  <Menu.Divider />
                  <Menu.Label>Remind me</Menu.Label>
                  {REMINDERS.map((reminder) => (
                    <Menu.Item
                      key={reminder.label}
                      leftSection={<IconBell size={14} />}
                      onClick={() =>
                        onAction(item, {
                          action: 'remind',
                          remindUntil: reminder.at().toISOString(),
                        })
                      }
                    >
                      {reminder.label}
                    </Menu.Item>
                  ))}
                </Menu.Dropdown>
              </Menu>
            </li>
          )
        })}
      </ul>
    </div>
  )
}
